
import React, { useState } from 'react';
import { Bell, X, Check, AlertCircle, Info, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRealTimeNotifications } from '@/hooks/useRealTimeNotifications';
import { toast } from 'sonner';

const NotificationCenter = () => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification, 
    clearAll 
  } = useRealTimeNotifications();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [isLoading, setIsLoading] = useState(false);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
      case 'generation':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'info':
      case 'feature':
        return <Info className="w-5 h-5 text-blue-400" />;
      case 'usage':
        return <Bell className="w-5 h-5 text-orange-400" />;
      case 'welcome':
        return <CheckCircle className="w-5 h-5 text-purple-400" />;
      default:
        return <Bell className="w-5 h-5 text-gray-400" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
      case 'generation':
        return 'bg-green-600';
      case 'processing':
        return 'bg-yellow-600';
      case 'error':
        return 'bg-red-600';
      case 'info':
      case 'feature':
        return 'bg-blue-600';
      case 'usage':
        return 'bg-orange-600';
      case 'welcome':
        return 'bg-purple-600';
      default:
        return 'bg-gray-600';
    }
  };

  const handleMarkAsRead = async (id: string) => {
    setIsLoading(true);
    try {
      await markAsRead(id);
      toast.success('Notification marked as read');
    } catch (error) {
      toast.error('Failed to mark notification as read');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    setIsLoading(true);
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNotification = (id: string) => {
    deleteNotification(id);
    toast.success('Notification deleted');
  };

  const handleClearAll = async () => {
    setIsLoading(true);
    try {
      await markAllAsRead(); // Use markAllAsRead due to RLS restrictions on DELETE
      setFilter('unread');
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = (notification: any) => {
    switch (notification.type) {
      case 'success':
        toast.info('Redirecting to image gallery...');
        break;
      case 'error':
        toast.info('Retrying generation...');
        break;
      case 'usage':
        toast.info('Redirecting to upgrade page...');
        break;
      case 'feature':
        toast.info('Opening batch upload feature...');
        break;
      default:
        toast.info('Action performed');
    }
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications.filter(n => !n.read);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-white" />
          <h2 className="text-xl font-semibold text-white">Notifications</h2>
          {unreadCount > 0 && (
            <Badge className="bg-red-500 text-white">
              {unreadCount}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilter(filter === 'all' ? 'unread' : 'all')}
            className="text-gray-300 hover:text-white hover:bg-white/10"
          >
            {filter === 'all' ? 'Show Unread' : 'Show All'}
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={isLoading}
              className="text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-50"
            >
              {isLoading ? 'Marking...' : 'Mark All Read'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
          >
            Clear All
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[500px]">
        <div className="space-y-3">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  notification.read
                    ? 'bg-white/5 border-white/10'
                    : 'bg-blue-500/10 border-blue-500/30 shadow-lg'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-white text-sm">
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge
                          className={`${getNotificationColor(notification.type)} text-white text-xs`}
                        >
                          {notification.type}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteNotification(notification.id)}
                          className="text-gray-400 hover:text-red-400 hover:bg-red-500/20 p-1 h-auto"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-gray-300 text-sm mb-2 pr-2">
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {notification.timestamp}
                      </span>
                      <div className="flex items-center gap-2">
                        {notification.actionable && (
                          <Button
                            size="sm"
                            onClick={() => handleAction(notification)}
                            className="text-xs bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {notification.actionText}
                          </Button>
                        )}
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkAsRead(notification.id)}
                            disabled={isLoading}
                            className="text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 disabled:opacity-50"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            {isLoading ? 'Marking...' : 'Mark Read'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default NotificationCenter;
